<script setup lang="ts">
  import { cn } from "@/lib/utils.ts"
  import { Circle } from "lucide-vue-next"
  import {
    RadioGroupIndicator,
    RadioGroupItem,
    useForwardProps,
    type RadioGroupItemProps,
  } from "radix-vue"
  import { computed, type HTMLAttributes } from "vue"
  import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
  } from "@/components/ui/form"

  const props = defineProps<
    RadioGroupItemProps & {
      class?: HTMLAttributes["class"]
      label: string
      name: string
      forceError?: boolean
    }
  >()

  const delegatedProps = computed(() => {
    const { class: _, ...delegated } = props

    return delegated
  })

  const forwardedProps = useForwardProps(delegatedProps)
</script>

<template>
  <FormField :name="props.name" type="radio">
    <FormItem
      :class="
        cn(
          'grid grid-cols-[auto,1fr] items-center gap-x-2 space-y-0 text-left',
          props.class,
        )
      "
    >
      <FormControl v-slot="{ error: formError, formItemId }">
        <RadioGroupItem
          v-bind="forwardedProps"
          :id="formItemId"
          :name="props.name"
          :value="props.value"
          :class="
            cn(
              'peer h-6 w-6 rounded-full border-2',
              'disabled:cursor-not-allowed disabled:border-secondary-400 data-[state=checked]:disabled:text-secondary-400',
              'focus-within:outline-primary-400',
              'data-[state=checked]:text-primary-400 data-[state=checked]:enabled:border-primary-400 data-[state=checked]:enabled:text-primary-400',
              (formError || props.forceError) && 'border-red-400',
              props.class,
            )
          "
        >
          <RadioGroupIndicator class="flex items-center justify-center">
            <Circle class="h-3 w-3 fill-current text-current" />
          </RadioGroupIndicator>
        </RadioGroupItem>
        <FormLabel v-if="label" :class="cn('text-base', 'font-normal')">
          <slot name="label">
            {{ label }}
          </slot>
        </FormLabel>
      </FormControl>
    </FormItem>
  </FormField>
</template>
